import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngBounds } from 'leaflet';
import { Share } from '../types/Share';
import { Box, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import { Person, LocationOn } from '@mui/icons-material';
import { useShares } from '../hooks/useShares';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
const defaultIcon = new Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function FitBounds({ shares }: { shares: Share[] }) {
  const map = useMap();

  useEffect(() => {
    if (shares.length === 0) return;

    const bounds = new LatLngBounds(
      shares.map((share) => [share.itemLocation.latitude, share.itemLocation.longitude])
    );

    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
  }, [map, shares]);

  return null;
}

export function ShareLocations() {
  const { shares, isLoading, error } = useShares();

  const validShares = useMemo(() => {
    return shares.filter(
      (share) =>
        share.itemLocation &&
        share.itemLocation.latitude !== 0 &&
        share.itemLocation.longitude !== 0
    );
  }, [shares]);

  const mapCenter = useMemo(() => {
    if (validShares.length === 0) {
      return { lat: 34.0522, lng: -118.2437 }; // Default to Los Angeles
    }
    const avgLat =
      validShares.reduce((sum, s) => sum + s.itemLocation.latitude, 0) /
      validShares.length;
    const avgLng =
      validShares.reduce((sum, s) => sum + s.itemLocation.longitude, 0) /
      validShares.length;
    return { lat: avgLat, lng: avgLng };
  }, [validShares]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading shares: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 4 }, height: 'calc(100vh - 64px)' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ mb: 0.5, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Map
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {validShares.length} shared item{validShares.length !== 1 ? 's' : ''} on the map
        </Typography>
      </Box>
      <Box
        sx={{
          height: { xs: 'calc(100% - 56px)', md: 'calc(100% - 72px)' },
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <MapContainer
          center={[mapCenter.lat, mapCenter.lng]}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
        >
          <FitBounds shares={validShares} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {validShares.map((share) => (
            <Marker
              key={share.recordName}
              position={[share.itemLocation.latitude, share.itemLocation.longitude]}
              icon={defaultIcon}
            >
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    {share.itemName.charAt(0).toUpperCase() + share.itemName.slice(1).toLowerCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {share.itemQuantity} {share.itemSize}
                  </Typography>
                  {share.itemSharedBy && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {share.itemSharedBy}
                      </Typography>
                    </Box>
                  )}
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 12 }} />}
                    label={`${share.itemLocation.latitude.toFixed(4)}, ${share.itemLocation.longitude.toFixed(4)}`}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1, fontSize: '0.7rem' }}
                  />
                </Box>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>
      {validShares.length === 0 && (
        <Box
          textAlign="center"
          py={8}
          sx={{
            borderRadius: 4,
            backgroundColor: '#ffffff',
            border: '2px dashed #e2e8f0',
            mt: 2,
          }}
        >
          <LocationOn sx={{ fontSize: 56, color: '#c7d2fe', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
            No locations yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shares with location data will appear on the map.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
